import { ValidationService,
         ClientValidationResult,
         PricingExcelApiResult,
         SpecialFixedPricingExcelApiValidationRequest,
         SpecialFixedPricingApiValidationRequestData,
         SpecialFixedPricingExcelApiValidationResponse,
         SpecialFixedPricingExcelDataRow,
         SpecialFixedPricingExcelTemplate,
         ExcelPropertyInfo,
         DateDetails,
         DateMetadata, 
         ExcelViewerErrorData } from "types";
import { ClientValidationError, ClientValidationErrorType } from "errors";
import { commonRegex, dateFormatter } from "utils";
import { SpecialFixedPricingExcelColumnHeaders, 
         SpecialFixedTemplateErrorKeys, 
         //SpecialPricingAdjustmentCodes, 
         YesOrNo } from "enums";
import { useSecurityService, useDateCheckerService, useExcelValidationHelpers } from "services";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { ExcelTemplateTypes } from "enums"; 

export function useSpecialFixedPricingTemplateValidationService(id: string, fileName: string, changeNotes: string, postValidationCallback?: (args: any[]) => void): ValidationService {
    let excelViewerErrorData: ExcelViewerErrorData[] = [];

    const { getAuthHeader, getUserEmail } = useSecurityService();

    const { checkValidDay, 
            checkValidMonth, 
            checkValidYear,
            checkIsPastDate } = useDateCheckerService();
    
    const { checkRequiredField, 
            checkExcelHeaders,
            standardizePropertyNames,
            trimValuesLeadingAndTrailingWhitespace,
            tryParseInt: tryParseIntGeneric } = useExcelValidationHelpers();

    const tryParseInt = (prop: ExcelPropertyInfo) => tryParseIntGeneric(SpecialFixedPricingExcelColumnHeaders, prop);

    const apiUrl = process.env.REACT_APP_SALES_PRICINGIMPORT_SPECIAL_FIXED_PRICING_VALIDATION_API,
          maxPricingDecimalPlaces = 4,
          regex = { dateFormat: commonRegex.dateFormats.mmddyyyy,
                    ambiguousDateMonth: commonRegex.dateFormats.monthFirstAmbiguous,
                    aboveMaxPrecision: commonRegex.maxDecimalPrecision(maxPricingDecimalPlaces),
                    allNines: commonRegex.allNines };

    //Sends a POST request containing uploaded and client-side validated excel data to Sales.PricingImport
    const postToValidationApi = async (clientValidatedExcel: SpecialFixedPricingExcelDataRow[]) => {
        try {
            const outboundModel: SpecialFixedPricingExcelApiValidationRequest = await convertToRequestModel(clientValidatedExcel);
            const fileId = `${fileName}-${id}.json`;
            const templateType = ExcelTemplateTypes.Fixed;

            const config: AxiosRequestConfig = {
                headers: await getAuthHeader()
            };

            // Request a SAS Upload URL
            const sasResponse = await axios.post<{ BlobUrl: string }>(
                `${process.env.REACT_APP_SALES_PRICINGIMPORT_GENERATE_SAS_URL}`,
                { fileId, templateType },
                config
            );

            const sasUrl = sasResponse.data.BlobUrl;
            //console.log(`SAS URL for upload: ${sasUrl}`)

            // Upload JSON payload to Blob
            await axios.put(
                sasUrl,
                JSON.stringify(outboundModel),
                {
                    headers: {
                        "x-ms-blob-type": "BlockBlob",
                        "Content-Type": "application/json"
                    }
                }
            );

            const container = "pricing-template-submissions";
            const blobPath = `${templateType}/${fileId}`;

            // Notify backend (APIM) with blob reference
            const notifyBackendResponse = await axios.post(
                `${apiUrl}`,
                {
                    Container: container,
                    BlobPath: blobPath,
                    TemplateFileName: outboundModel.TemplateFileName,
                    SubmitterEmail: outboundModel.SubmitterEmail,
                    ChangeNotes: outboundModel.ChangeNotes,
                    CosmosGUID: outboundModel.CosmosGUID,
                },
                config
            );

            return {
                response: notifyBackendResponse.data as SpecialFixedPricingExcelApiValidationResponse,
                postError: "",
                statusCode: notifyBackendResponse.status
            } as PricingExcelApiResult;

        } catch (ex: any) {
            const error = ex as AxiosError;
            const status = error.response?.status ?? 0;

            return {
                response: error.response?.data as SpecialFixedPricingExcelApiValidationResponse,
                postError: error.message ?? "Error posting to validation API.",
                statusCode: status
            } as PricingExcelApiResult;
        }
    };   
        
    const doClientSideValidation = async (excel: SpecialFixedPricingExcelTemplate[] | null, skipNumHeaders: number) => {
        let errText: string = "";
        const errorList: ExcelViewerErrorData[] = [],
              panelErrors: string[] = [],
              validFlags = Object.values<string>(YesOrNo);
        //      validAdjTypes = Object.keys(SpecialPricingAdjustmentCodes)
        //                            .filter(x => !isNaN(Number(x)));

        excelViewerErrorData = [];
        const extraHeaderValidation = () => {
            //Checking for data in a few fields of the first data row to ensure that this isn't a blank template
            if (!excel) return;
            if (skipNumHeaders < 1) {
                if ( !( excel[0]["CUSTOMERNUMBER"] 
                     || excel[0]["ITEMNUMBER"] 
                     || excel[0]["BEGINDATE"]
                     || excel[0]["ENDDATE"]
                     || excel[0]["UOM"]
                     || excel[0]["SUPPLIERRESPONSIBLE"]
                     || excel[0]["PRICE"]
                     || excel[0]["ADJY/N"]   
                     || excel[0]["JOBTYPE"] ) )
                {
                    throw new ClientValidationError(ClientValidationErrorType.NoData, 
                                                    "Excel file does not appear to contain any valid data rows.", 
                                                    null);
                }
            }
        };
        checkExcelHeaders(excel, skipNumHeaders, extraHeaderValidation);

        //Null suppression because the above call to checkExcelHeaders will 
        //throw a ClientValidationError on null or empty excel data, so there's no need for another check.
        const excelData = convertToValidationModel(excel!, skipNumHeaders);

        //Loop through rows.  
        for (const [index, row] of excelData.entries()) {
            //1 to skip the header (keys) row, 1 to offset zero-indexing
            const rowNumber = index + 2 + skipNumHeaders;

            await doDateValidation(errorList, row, rowNumber);

            for (const prop of Object.getOwnPropertyNames(row)) {
                const thisCellValue = (`${row[prop as keyof SpecialFixedPricingExcelDataRow] ?? ""}`).trim(),
                      propNameKey = prop as keyof typeof SpecialFixedPricingExcelColumnHeaders,
                      propColumnName = `column "${SpecialFixedPricingExcelColumnHeaders[propNameKey]}"`,
                      errorKey = prop as keyof typeof SpecialFixedTemplateErrorKeys;
                switch (prop) {
                    case "autoAdjust":
                    case "adjust":
                    case "supplierResponsible":
                    case "promoFlag":
                    case "floatFlag":
                        if (!thisCellValue) {
                            row[prop] = YesOrNo.No;
                            break;
                        }
                        else {
                            const flagValue = (`${thisCellValue}`).toUpperCase();
                            if (validFlags.includes(flagValue)) {
                                row[prop] = flagValue;
                            } else {
                                errText = `Invalid value "${thisCellValue}" for ${propColumnName}, this field can only include ` + 
                                          `one of the following values: ${validFlags.join(", ")}`;
                                console.log("bad Y/N formats");
                                errorList.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys[errorKey], [errText]));
                            }
                        }
                        break;
                    case "customerNumber":
                    case "customerShipto":
                    case "itemNumber":
                            const thisPropInfo = { propName: propNameKey,
                                                   value: thisCellValue,
                                                   dataRow: rowNumber,
                                                   errorKey: errorKey,
                                                   log: errorList } as ExcelPropertyInfo;
                            if (!checkIfRequiredFieldIsNotEmpty(thisPropInfo)) {
                                break;
                            } else if (thisCellValue !== "" && parseInt(thisCellValue) < 0) {
                                errText = `Invalid value "${thisCellValue}" for ${propColumnName}, cannot be a negative number.`;
                                console.log("bad NEG NUM formats");
                                errorList.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys[errorKey], [errText]));
                            }
                        break;
                    case "price":
                    case "adjust1Amount":
                    case "adjust1Percentage":
                    case "adjust2Amount":
                    case "adjust2Percentage":
                    case "adjust3Amount":
                    case "adjust3Percentage":
                        if (regex.aboveMaxPrecision.test(thisCellValue)) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, this field can have no more ` + 
                                      `than ${maxPricingDecimalPlaces} decimal places.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                    case "employee":
                    case "jobType":
                        if (thisCellValue !== "" && parseInt(thisCellValue) < 0) {
                            errText = `Invalid value "${thisCellValue}" for ${propColumnName}, cannot be a negative number.`;
                            errorList.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys[errorKey], [errText]));
                        }
                        break;
                    // case "adjustmentType":
                    //     if (!(parseInt(thisCellValue) in SpecialPricingAdjustmentCodes)) {
                    //         errText = `Invalid value "${thisCellValue}" for ${propColumnName}, ` + 
                    //                   `valid options: ${validAdjTypes.join(", ")}.`;
                    //         errorList.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys[errorKey], [errText]));
                    //     }
                    //    break;
                }
            }
        }

        //When we're done looping through rows
        if (postValidationCallback) postValidationCallback([...excelViewerErrorData, ...errorList]);
        errorList.forEach(x => panelErrors.push(`Data row ${x.row} - ${x.info.join(" | ")}`));
        return { requestData: excelData, 
                 errorList: panelErrors, 
                 errorCount: panelErrors.length } as ClientValidationResult;
    };

    //Strips spaces and lower case letters out of Excel template property names
    const standardizeExcelPropertyNameFormat = (rawExcel: any[]) => standardizePropertyNames<SpecialFixedPricingExcelTemplate>(rawExcel);

    //Logs error on missing required field 
    const checkIfRequiredFieldIsNotEmpty = (props: ExcelPropertyInfo) => checkRequiredField(SpecialFixedPricingExcelColumnHeaders, props);

    //Checks for invalid date formats, past dates, and end date before begin date
    const doDateValidation = async (errors: ExcelViewerErrorData[], model: SpecialFixedPricingExcelDataRow, rowNumber: number) => {
        const format: RegExp = regex.dateFormat,
              ambiguousMatch: RegExp = regex.ambiguousDateMonth,
              beginErrors: ExcelViewerErrorData[] = [],
              endErrors: ExcelViewerErrorData[] = [],
              begin = model.beginDate?.toString().match(format),
              end = model.endDate?.toString().match(format),
              bothDateColumns = `${SpecialFixedTemplateErrorKeys.beginDate}, ${SpecialFixedTemplateErrorKeys.endDate}`;
        
        let errText: string = "";

        const beginDate = { year: parseInt(begin?.groups?.year ?? ""),
                            month: parseInt(begin?.groups?.month ?? ""),
                            day: parseInt(begin?.groups?.day ?? "") } as DateDetails,
              endDate =   { year: parseInt(end?.groups?.year ?? ""),
                            month: parseInt(end?.groups?.month ?? ""),
                            day: parseInt(end?.groups?.day ?? "") } as DateDetails,

              beginDateMetadata = { fullDateString: model.beginDate,
                                    date: beginDate,
                                    dateType: SpecialFixedTemplateErrorKeys.beginDate,
                                    dateColumnName: SpecialFixedPricingExcelColumnHeaders.beginDate,
                                    rowNumber: rowNumber,
                                     } as DateMetadata,
              endDateMetadata =   { fullDateString: model.endDate,
                                    date: endDate,
                                    dateType: SpecialFixedTemplateErrorKeys.endDate,
                                    dateColumnName: SpecialFixedPricingExcelColumnHeaders.endDate,
                                    rowNumber: rowNumber } as DateMetadata;

        const isSameYear = beginDate.year === endDate.year;

        const flags = { sameYear: isSameYear,
                        sameMonth: (isSameYear && beginDate.month === endDate.month),
                        invalidMonth: false,
                        invalidYear: false,
                        invalidDay: false,
                        badFormat: false,
                        badBeginDate: false,
                        badEndDate: false,
                        allNinesEndDate: false };

        //priceBeginDate checks
        if (model.beginDate) {
            checkIsPastDate(beginDateMetadata, beginErrors);
            flags.invalidYear = !checkValidYear(beginDateMetadata, beginErrors);
            flags.invalidMonth = !checkValidMonth(beginDateMetadata, beginErrors);
            flags.invalidDay = !checkValidDay(beginDateMetadata, beginErrors, flags.invalidMonth, flags.invalidYear);
            flags.badFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;
            flags.badBeginDate = flags.badFormat;
        }

        //priceEndDate checks
        if (model.endDate) {
            flags.allNinesEndDate = regex.allNines.test(model.endDate);
            if (flags.allNinesEndDate) {
                flags.badEndDate = true;
                errText = `Invalid end date of 99999999. Please use 12319999 instead.`
                errors.push(new ExcelViewerErrorData(rowNumber, bothDateColumns, [errText]));
            }

            if (!flags.allNinesEndDate) {
                checkIsPastDate(endDateMetadata, endErrors);
                flags.invalidYear = !checkValidYear(endDateMetadata, endErrors);
                flags.invalidMonth = !checkValidMonth(endDateMetadata, endErrors);
                flags.invalidDay = !checkValidDay(endDateMetadata, endErrors, flags.invalidMonth, flags.invalidYear);
                if (!flags.badFormat) flags.badFormat = flags.invalidDay || flags.invalidYear || flags.invalidMonth;
                flags.badEndDate = flags.badFormat;
                
                //Date range checks
                if( (flags.sameYear && beginDate.month > endDate.month)
                 || (flags.sameMonth && beginDate.day > endDate.day)
                 || (endDate.year < beginDate.year) ) {
                    flags.badEndDate = true;
                    flags.badBeginDate = true;
                    errText = `Invalid date range of "${model.beginDate}" - "${model.endDate}", ` + 
                              `reason: end date is before begin date.`
                    errors.push(new ExcelViewerErrorData(rowNumber, bothDateColumns, [errText]));
                }
            }
        }
            
        //Ambiguity checks
        const hasAmbiguousBeginDate = ambiguousMatch.test(model.beginDate?.toString() ?? ""),
              hasAmbiguousEndDate = !flags.allNinesEndDate && ambiguousMatch.test(model.endDate?.toString() ?? "");

        if (hasAmbiguousBeginDate) {
            flags.badBeginDate = true;
            errText = "Begin date is ambiguous - add forward-slash ('/') separators between month, day, and year, " + 
                    "or use the full 8-digit MMDDYYYY format with leading zeroes for month and/or day where appropriate.";
            errors.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys.beginDate, [errText]));
        }
        if (hasAmbiguousEndDate) {
            flags.badEndDate = true;
            errText = "End date is ambiguous - add forward-slash ('/') separators between month, day, and year, " + 
                    "or use the full 8-digit MMDDYYYY format with leading zeroes for month and/or day where appropriate.";
            errors.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys.endDate, [errText]));
        }

        //Aggregation
        if (flags.badBeginDate) {
            errText = "Begin date appears not to be in correct format.";
            errors.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys.beginDate, [errText]));
        } else {
            //These errors are only valuable if begin date format is correct
            errors.push(...beginErrors);
        }

        if (flags.badEndDate) {
            errText = 'End date appears not to be in correct format.';
            errors.push(new ExcelViewerErrorData(rowNumber, SpecialFixedTemplateErrorKeys.endDate, [errText]));
        } else {
            //These errors are only valuable if end date format is correct
            errors.push(...endErrors);
        }

        if (flags.badFormat) {
            console.log("bad date formats");
            errText = `Ensure that all dates are in the correct format: MMDDYYYY`;
            errors.push(new ExcelViewerErrorData(rowNumber, bothDateColumns, [errText]));
        }

        //Because we're accepting MMDDYYYY values for months 1-9 even without a leading zero, 
        //we need to standardize the format for the validation API
        if (model.beginDate) model.beginDate = dateFormatter.MMDDYYYY(beginDate);
        if (model.endDate && !flags.allNinesEndDate) model.endDate = dateFormatter.MMDDYYYY(endDate);
    };

    //Model conversions
    const convertToValidationModel = (rawExcelTemplate: SpecialFixedPricingExcelTemplate[], skipNumHeaders: number) => {
        let validationModel: SpecialFixedPricingExcelDataRow[] = [];
        const excelTemplate = trimValuesLeadingAndTrailingWhitespace<SpecialFixedPricingExcelTemplate>(rawExcelTemplate);

        for (let i = skipNumHeaders; i < excelTemplate.length; i++) {
            const fieldIsEmpty = (prop: ExcelPropertyInfo) => (prop.value?.toString().trim().length ?? 0) === 0,
                  tryParseIfNotNull = (prop: ExcelPropertyInfo) => (fieldIsEmpty(prop) ? null : tryParseInt(prop)),
                  rowData = {} as SpecialFixedPricingExcelDataRow,
                  excelData = excelTemplate[i],
                  propInfo = { propName: "",
                               value: null,
                               dataRow: i + 2,
                               log: [] } as ExcelPropertyInfo;

            for (const property of Object.getOwnPropertyNames(excelData)) {
                const key = property as keyof typeof excelData;
                if (excelData[key] && typeof(excelData[key] === 'string')) {
                    excelData[key] = (`${excelData[key]}`).trim();
                }
            }

            //Non-integer fields

            rowData.name = excelData.NAME ?? "";
            rowData.beginDate = excelData.BEGINDATE ?? "";
            rowData.endDate = excelData.ENDDATE ?? "";

            rowData.manufacturer = excelData.MANUFACTURER ?? "";
            rowData.description = excelData.DESCRIPTION ?? "";
            rowData.uom = excelData.UOM ?? "";

            //Defaulting this to zero seemed like maybe a bad idea
            rowData.price = excelData.PRICE;

            rowData.contractNumber = excelData.CONTRACTNUMBER ?? "";
            rowData.contractName = excelData.CONTRACTNAME ?? "";
            rowData.contractDescription = excelData.CONTRACTDESCRIPTION ?? "";
            rowData.supplierResponsible = excelData.SUPPLIERRESPONSIBLE ?? "";
            rowData.comments = excelData.COMMENTS ?? "";

            rowData.floatFlag = excelData.FLOATFLAG ?? "";
            //rowData.nationalAccount = excelData.NATIONALACCT ?? "";

            rowData.promoFlag = excelData.PROMOFLAG ?? "";
            rowData.adjust = excelData["ADJY/N"] ?? "";
            rowData.autoAdjust = excelData["AUTOADJY/N"] ?? "";

            rowData.adjust1Amount = excelData.ADJ1AMT ?? "";
            rowData.adjust1Percentage = excelData["ADJ1%"] ?? "";
            rowData.adjust2Amount = excelData.ADJ2AMT ?? "";
            rowData.adjust2Percentage = excelData["ADJ2%"] ?? "";
            rowData.adjust3Amount = excelData.ADJ3AMT ?? "";
            rowData.adjust3Percentage = excelData["ADJ3%"] ?? "";

            //Integer fields

            //Customer number
            propInfo.propName = SpecialFixedTemplateErrorKeys.customerNumber;
            propInfo.value = excelData.CUSTOMERNUMBER;
            rowData.customerNumber = tryParseIfNotNull(propInfo);

            //Customer shipto
            propInfo.propName = SpecialFixedTemplateErrorKeys.customerShipto;
            propInfo.value = excelData.SHIPTO;
            rowData.customerShipto = tryParseIfNotNull(propInfo);

            //Item number
            propInfo.propName = SpecialFixedTemplateErrorKeys.itemNumber;
            propInfo.value = excelData.ITEMNUMBER;
            rowData.itemNumber = tryParseIfNotNull(propInfo);

            //Employee number
            //This was misspelled on the original template and will accept either spelling (prioritizing correct)
            const employeeProp = excelData.EMPLOYEE ? excelData.EMPLOYEE.toString().trim().length 
                                                    : 0;
            propInfo.propName = SpecialFixedTemplateErrorKeys.employee;
            propInfo.value = employeeProp != null && employeeProp > 0 ? excelData.EMPLOYEE : excelData.EMPLYEE;
            rowData.employee = tryParseIfNotNull(propInfo);

            //Job type
            propInfo.propName = SpecialFixedTemplateErrorKeys.jobType;
            propInfo.value = excelData.JOBTYPE;
            rowData.jobType = tryParseIfNotNull(propInfo);

            //Adjustment type
            propInfo.propName = SpecialFixedTemplateErrorKeys.adjust1Type;
            propInfo.value = excelData.ADJ1TYPE;
            rowData.adjust1Type = tryParseIfNotNull(propInfo);

            propInfo.propName = SpecialFixedTemplateErrorKeys.adjust2Type;
            propInfo.value = excelData.ADJ2TYPE;
            rowData.adjust2Type = tryParseIfNotNull(propInfo);

            propInfo.propName = SpecialFixedTemplateErrorKeys.adjust3Type;
            propInfo.value = excelData.ADJ3TYPE;
            rowData.adjust3Type = tryParseIfNotNull(propInfo);

            if (propInfo.log && propInfo.log.length > 0) {
                excelViewerErrorData.push(...propInfo.log);
            }
            validationModel.push(rowData);
        }
        return validationModel;
    };

    //Client validation model => validation API request model
    const convertToRequestModel = (validatedExcel: SpecialFixedPricingExcelDataRow[]) => {
        const requestTemplate = validatedExcel.map(row => 
            { 
                return { CustomerNumber: `${row.customerNumber}`,
                         CustomerShiptoNumber: `${row.customerShipto}`,
                         CustomerName: `${row.name}`,

                         ItemDescription: `${row.description}`,
                         ItemNumber: `${row.itemNumber}`,
                         ManufacturerNumber: `${row.manufacturer}`,
                         ContractNumber: `${row.contractNumber}`,
                         ContractName: `${row.contractName}`,
                         ContractDescription: `${row.contractDescription}`,
            
                         PriceAmount: `${row.price}`,
                         PriceUnitOfMeasure: `${row.uom}`,
                         PriceBeginDate: `${row.beginDate}`,
                         PriceEndDate: `${row.endDate}`,

                         EmployeeNumber: row.employee ? row.employee : null,
                         JobType: row.jobType ? row.jobType : null,

                         SupplierIndicator: `${row.supplierResponsible}`,
                         Comment: `${row.comments}`,
                         PromoFlag: `${row.promoFlag}`,
                         //NationalAccount: `${row.nationalAccount}`,
                         FloatFlag: `${row.floatFlag}`,

                         AdjustmentFlag: `${row.adjust}`,
                         AutoFlag: `${row.autoAdjust}`,

                         Adjustment1Type: `${row.adjust1Type}`,
                         Adjustment1Amount: `${row.adjust1Amount}`,
                         Adjustment1Percent: `${row.adjust1Percentage}`,

                         Adjustment2Type: `${row.adjust2Type ?? ""}`,
                         Adjustment2Amount: `${row.adjust2Amount}`,
                         Adjustment2Percent: `${row.adjust2Percentage}`,

                         Adjustment3Type: `${row.adjust3Type ?? ""}`,
                         Adjustment3Amount: `${row.adjust3Amount}`,
                         Adjustment3Percent: `${row.adjust3Percentage}` } as SpecialFixedPricingApiValidationRequestData;
            }
        );
        const requestModel: SpecialFixedPricingExcelApiValidationRequest = 
        {
            TemplateFileName: fileName,
            CosmosGUID: id,
            SubmitterEmail: getUserEmail(),
            ChangeNotes: changeNotes,
            SpecialFixedPricingTemplate: requestTemplate
        };
        return requestModel;
    };
    
    //Exports
    const testingExports = { doDateValidation,
                             convertToValidationModel,
                             convertToRequestModel };

    return {
        doClientSideValidation, 
        postToValidationApi,
        standardizeExcelPropertyNameFormat,
        testingExports
    };
}
